/**
 * A page imported from a PDF, redrawn exactly and editable in place.
 *
 * Everything this kind is: how it is made, how it is read back, what it looks
 * like on the page, and how it is edited. Only an import makes one, so the
 * palette does not offer it.
 */

import { readNumber, readString } from '@/domain/json'
import { readFrames } from '@/domain/facsimile/read'
import type { FixedPageSection } from '@/domain/section'
import FixedPageBlock from '@/sections/fixedPage/FixedPageBlock.vue'
import FixedPageInspector from '@/sections/fixedPage/FixedPageInspector.vue'
import type { SectionDefinition } from '@/sections/types'

/** A4, in points: what an empty one would be, were one ever made by hand. */
const A4 = { width: 595.28, height: 841.89 }

/** Only a picture, as a data URL, ever reaches the background. */
function readPicture(value: string): string {
  return /^data:image\/(png|jpeg|webp);base64,/.test(value) ? value : ''
}

export const fixedPageSection: SectionDefinition<'fixedPage', FixedPageSection> = {
  kind: 'fixedPage',
  band: 'structure',
  inPalette: false,

  icon: ['M3.5 1.5h9v13h-9z', 'M5.5 4.5h5', 'M5.5 7h5', 'M5.5 9.5h3', 'M10 10.5l1.5 1.5'],

  create: (id, title) => ({
    id,
    kind: 'fixedPage',
    title,
    width: A4.width,
    height: A4.height,
    background: '',
    frames: [],
    sourceFile: '',
    sourcePage: 0,
  }),

  read: ({ id, title, stored }) => ({
    id,
    kind: 'fixedPage',
    title,
    width: readNumber(stored, 'width') || A4.width,
    height: readNumber(stored, 'height') || A4.height,
    background: readPicture(readString(stored, 'background')),
    frames: readFrames(stored),
    sourceFile: readString(stored, 'sourceFile'),
    sourcePage: readNumber(stored, 'sourcePage'),
  }),

  block: FixedPageBlock,
  inspector: FixedPageInspector,
}
